/**
 * LandingPageTab Component
 *
 * Provides a personalized dashboard of quick-access navigation links
 * based on user role and permissions. This is the default tab for the
 * Community Details page.
 *
 * Icons match the side navigation in GuideCommunityPages.tsx for consistency.
 */

import { useAuthState } from '@auth/hooks';
import { routes as communityRoutes } from '@communities/routes';
import { FEATURE_FLAGS, UserRoles } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { routes as auditRoutes } from '@internal-audit/routes';
import { Card, Complaint, Flag, Heading, Icon, IconSize, Search, ServerProcesses, TestBeaker, Text } from '@soteria/design-system-ve';
import type { ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';

import { config } from '../../../config';

import * as styles from './LandingPageTab.css';

interface NavigationLink {
  key: string;
  title: string;
  description: string;
  icon: ComponentType;
  path: string;
  isVisible: boolean;
  isExternal?: boolean;
}

interface LandingPageTabProps {
  /** Community ID for route generation */
  communityId: string;
  /** Community Group ID for permission checks */
  communityGroupId?: string;
}

interface RoleAccess {
  hasQAAccess: boolean;
  hasAdminAccess: boolean;
  hasModerationAccess: boolean;
}

function buildNavigationLinks(communityId: string, roleAccess: RoleAccess, isModerationEnabled: boolean): NavigationLink[] {
  return [
    {
      key: 'classification-testing',
      title: 'Classification Testing',
      description: 'Test content against classification policies and view results',
      icon: TestBeaker,
      path: communityRoutes.communities.classificationTesting.root(communityId),
      isVisible: true,
    },
    {
      key: 'content-search',
      title: 'Content Search',
      description: 'Search and browse community content',
      icon: Search,
      path: communityRoutes.communities.contentSearch.root(communityId),
      isVisible: true,
    },
    {
      key: 'audits',
      title: 'Audits',
      description: 'Review audit sessions and reports for this community',
      icon: Flag,
      path: auditRoutes.internalAudit.community.audits(communityId),
      isVisible: roleAccess.hasQAAccess,
    },
    {
      key: 'moderation',
      title: 'Moderate',
      description: 'Moderate community content and manage cases',
      icon: Complaint,
      path: communityRoutes.communities.moderation.root(communityId),
      isVisible: isModerationEnabled && roleAccess.hasModerationAccess,
    },
    {
      key: 'synesis',
      title: 'Synesis Data Management',
      description: 'Manage community data in external Synesis tool',
      icon: ServerProcesses,
      path: config.externalServices.synesisUrl ?? '',
      isVisible: roleAccess.hasAdminAccess && Boolean(config.externalServices.synesisUrl),
      isExternal: true,
    },
  ];
}

function LandingPageTabComponent({ communityId, communityGroupId }: LandingPageTabProps) {
  const navigate = useNavigate();
  const { hasRoleAccess } = useAuthState();
  const isModerationEnabled = useIsFeatureEnabled(FEATURE_FLAGS.MODERATION);

  // Permission checks (matches CommunityHeaderSection.tsx and GuideCommunityPages.tsx)
  const hasAdminAccess = hasRoleAccess(UserRoles.ADMIN, communityGroupId, communityId);
  const roleAccess: RoleAccess = {
    hasQAAccess: hasRoleAccess(UserRoles.QA, communityGroupId, communityId),
    hasAdminAccess,
    hasModerationAccess: hasRoleAccess(UserRoles.MODERATOR, communityGroupId, communityId) || hasAdminAccess,
  };

  const allLinks = buildNavigationLinks(communityId, roleAccess, isModerationEnabled);
  const visibleLinks = allLinks.filter(link => link.isVisible);

  // Handle navigation - external links open in new tab
  const handleCardClick = (link: NavigationLink) => {
    if (link.isExternal) {
      window.open(link.path, '_blank', 'noopener,noreferrer');
    } else {
      navigate(link.path);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.navigationGrid}>
        {visibleLinks.map(link => (
          <Card
            key={link.key}
            aria-label={link.isExternal ? `${link.title} (opens in new tab)` : link.title}
            className={styles.navigationCard}
            elevation="low"
            padding="large"
            onClick={() => handleCardClick(link)}
          >
            <div className={styles.iconContainer}>
              <Icon icon={link.icon} iconSize={IconSize.XLarge} />
            </div>
            <Heading className={styles.cardTitle} level="h3" size="lg">
              {link.title}
            </Heading>
            <Text className={styles.cardDescription} color="secondary" size="sm">
              {link.description}
            </Text>
          </Card>
        ))}
      </div>
    </div>
  );
}

export const LandingPageTab = withAppInsightsTracking(LandingPageTabComponent, 'CommunityOverviewTab');
